/**
 * Utility functions to track which booking statuses have been viewed by the user
 * Stores data in localStorage: bookingViewedStatuses_{userId} = { bookingId: { status, viewedAt } }
 */

const getStorageKey = (userId) => {
  return userId ? `bookingViewedStatuses_${userId}` : 'bookingViewedStatuses';
};

/**
 * Get all viewed booking statuses from localStorage for a specific user
 * @param {string} userId - The user ID (optional, for scoping)
 * @returns {Object} Map of bookingId -> { status, viewedAt }
 */
export const getViewedBookingStatuses = (userId = null) => {
  try {
    const storageKey = getStorageKey(userId);
    const stored = localStorage.getItem(storageKey);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('[bookingViewedTracker] Error reading from localStorage:', error);
    return {};
  }
};

/**
 * Get the last viewed status for a specific booking
 * @param {string} bookingId - The booking ID
 * @param {string} userId - The user ID (optional, for scoping)
 * @returns {Object|null} { status, viewedAt } or null if not viewed
 */
export const getLastViewedStatus = (bookingId, userId = null) => {
  if (!bookingId) return null;
  const viewedStatuses = getViewedBookingStatuses(userId);
  return viewedStatuses[bookingId] || null;
};

/**
 * Mark a booking status as viewed
 * @param {string} bookingId - The booking ID
 * @param {string} status - The current status (e.g., 'PENDING', 'CONFIRMED')
 * @param {string} userId - The user ID (optional, for scoping)
 */
export const markBookingAsViewed = (bookingId, status, userId = null) => {
  if (!bookingId || !status) return;
  
  try {
    const storageKey = getStorageKey(userId);
    const viewedStatuses = getViewedBookingStatuses(userId);
    viewedStatuses[bookingId] = {
      status,
      viewedAt: new Date().toISOString()
    };
    localStorage.setItem(storageKey, JSON.stringify(viewedStatuses));
    
    // Dispatch custom event to notify hooks of the change
    window.dispatchEvent(new CustomEvent('bookingViewed', { 
      detail: { bookingId, status, userId } 
    }));
  } catch (error) {
    console.error('[bookingViewedTracker] Error saving to localStorage:', error);
  }
};

/**
 * Check if a booking has unviewed status changes
 * @param {Object} booking - The booking object with id and status
 * @param {string} userId - The user ID (optional, for scoping)
 * @returns {boolean} True if status has changed since last view
 */
export const hasUnviewedStatusChange = (booking, userId = null) => {
  if (!booking?.id || !booking?.status) return false;
  
  const lastViewed = getLastViewedStatus(booking.id, userId);
  
  // If never viewed, consider it as unviewed if status is PENDING (initial state)
  // For PENDING bookings, we want to show notification immediately
  if (!lastViewed) {
    return booking.status === 'PENDING';
  }
  
  // If status has changed since last view, it's unviewed
  return lastViewed.status !== booking.status;
};

/**
 * Check if a booking is NEW (never been viewed before and has PENDING status)
 * This is used to show notifications only for NEW bookings, not status changes
 * @param {Object} booking - The booking object with id and status
 * @param {string} userId - The user ID (optional, for scoping)
 * @returns {boolean} True if booking is NEW (never viewed + PENDING)
 */
export const hasNewBooking = (booking, userId = null) => {
  if (!booking?.id || !booking?.status) return false;
  
  const lastViewed = getLastViewedStatus(booking.id, userId);
  
  // A NEW booking is one that has never been viewed AND has PENDING status
  return !lastViewed && booking.status === 'PENDING';
};

/**
 * Clear viewed status for a booking (useful for testing or reset)
 * @param {string} bookingId - The booking ID
 * @param {string} userId - The user ID (optional, for scoping)
 */
export const clearViewedStatus = (bookingId, userId = null) => {
  if (!bookingId) return;
  
  try {
    const storageKey = getStorageKey(userId);
    const viewedStatuses = getViewedBookingStatuses(userId);
    delete viewedStatuses[bookingId];
    localStorage.setItem(storageKey, JSON.stringify(viewedStatuses));
  } catch (error) {
    console.error('[bookingViewedTracker] Error clearing viewed status:', error);
  }
};

/**
 * Clear all viewed statuses for a user (useful for testing or logout)
 * @param {string} userId - The user ID (optional, clears all if not provided)
 */
export const clearAllViewedStatuses = (userId = null) => {
  try {
    const storageKey = getStorageKey(userId);
    localStorage.removeItem(storageKey);
  } catch (error) {
    console.error('[bookingViewedTracker] Error clearing all viewed statuses:', error);
  }
};

